import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { 
  GET_JOB, 
  GET_PUBLIC_JOB, 
  GET_ASSOCIATED_CLEANING_JOB, 
  GET_JOB_CHECKLIST_COMPLETIONS,
  GET_PROPERTY_RESERVATIONS,
  GET_PUBLIC_PROPERTY_RESERVATIONS,
  GET_NEXT_RESERVATION
} from '../graphql/queries';
import { 
  UPDATE_JOB_STATUS, 
  UPDATE_PUBLIC_JOB_STATUS,
  GENERATE_SHARE_TOKEN,
  MARK_INSPECTION_COMPLETE
} from '../graphql/mutations';
import { 
  format, 
  parseISO, 
  isValid, 
  isAfter,
  isToday 
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useAuth } from '../contexts/AuthContext';
import JobChecklistModal from '../components/JobChecklistModal';
import CreateWorkOrderModal from '../components/work-orders/CreateWorkOrderModal';
import CreateLostFoundModal from '../components/CreateLostFoundModal';
import ReservationTypeBadge from '../components/ReservationTypeBadge';
import { toast } from 'react-hot-toast';
import {
  OwnerArrivalIndicator,
  OwnerDepartureIndicator
} from '../components/job-indicators';
import {
  FlipIndicator,
  WalkthroughIndicator,
  LateCheckoutIndicator,
  CustomJobIndicator,
  DateOverrideIndicator,
  JobCategoryPill,
  AreaIndicator
} from '../components/job-indicators';
import JobHeader from '../components/job-details/jobHeader';
import JobMeta from '../components/job-details/jobMeta';
import JobChecklistSection from '../components/job-details/JobChecklistSection';
import JobAdminBar from '../components/job-details/jobAdminBar';
import JobHomeDetails from '../components/job-details/jobHomeDetails';
import JobStagingPhotos from '../components/job-details/jobStagingPhotos';
import JobPropertyInfo from '../components/job-details/jobPropertyInfo';
import JobPropertyNotes from '../components/job-details/jobPropertyNotes';
import { buildJobStatusGPSInput, captureJobLocationSilently } from '../components/job-details/job-gps-utils';
import usePropertyManagementDisplay from '../hooks/usePropertyManagementDisplay';

// Helper functions to check job properties
const isOwnerArrival = (job) => {
  return job.isOwnerArrival === true;
};

const isOwnerDeparture = (job) => {
  return job.isOwnerDeparture === true;
};

// Add this helper function to get a display-friendly service type name
const getServiceTypeDisplayName = (job) => {
  if (job?.serviceType?.title) {
    return job.serviceType.title;
  } else if (job?.category === 'INSPECTION') {
    return 'Inspection';
  } else if (job?.cleaningType === 'FLIP') {
    return 'Turnover';
  } else {
    return 'Clean'; // Default fallback
  }
};

export default function JobDetails({ isPublicView = false, accessToken = null, jobId: propJobId = null, onJobStatusChange = null }) {
  const { jobId: routeJobId } = useParams();
  const jobId = propJobId || routeJobId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const client = useApolloClient();
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const { pathname, search } = useLocation();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [isCreateWorkOrderModalOpen, setIsCreateWorkOrderModalOpen] = useState(false);
  const [isCreateLostFoundModalOpen, setIsCreateLostFoundModalOpen] = useState(false);
  
  // State for photo viewer modal
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  // Check if the page is being viewed in shared mode (via a shared link)
  const isSharedView = search.includes('share=true') || isPublicView;

  // Use different queries based on whether this is a public view or not
  const { loading: publicJobLoading, error: publicJobError, data: publicJobData, refetch: publicJobRefetch } = useQuery(GET_PUBLIC_JOB, {
    variables: { id: jobId, token: accessToken },
    fetchPolicy: 'network-only',
    skip: !isPublicView,
    onError: (error) => console.error('Error loading public job:', error),
    onCompleted: (data) => {
      if (data) logJobData(data);
    }
  });

  const { loading: privateJobLoading, error: privateJobError, data: privateJobData, refetch: privateJobRefetch } = useQuery(GET_JOB, {
    variables: { id: jobId },
    fetchPolicy: 'network-only',
    skip: isPublicView,
    onCompleted: (data) => {
      if (data) logJobData(data);
    }
  });

  // Combine the loading and error states from both queries
  const jobLoading = publicJobLoading || privateJobLoading;
  const jobError = isPublicView ? publicJobError : privateJobError;
  
  // Use the appropriate data source
  const jobData = isPublicView ? publicJobData : privateJobData;
  // Define job here to avoid reference errors
  const job = jobData?.job || jobData?.publicJob;

  // Determine if this is an inspection job
  const isInspectionJob = job?.category === 'INSPECTION';
  
  // Fetch associated cleaning job if this is an inspection job
  const { data: associatedCleaningJobData, loading: associatedCleaningJobLoading } = useQuery(GET_ASSOCIATED_CLEANING_JOB, {
    variables: { inspectionJobId: jobId },
    skip: !isInspectionJob || isPublicView, // Only fetch for authenticated inspection jobs
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      // Data loading complete
    },
    onError: (error) => {
      console.error('Error fetching associated cleaning job:', error);
    }
  });
  
  // Store the associated cleaning job for reference
  const associatedCleaningJob = associatedCleaningJobData?.associatedCleaningJob;

  const { data: jobChecklistCompletionsData, refetch: refetchJobChecklistCompletions } = useQuery(GET_JOB_CHECKLIST_COMPLETIONS, {
    variables: { jobId: job?.id },
    skip: !job?.id || !job?.serviceType?.useChecklistStyle || !job?.serviceType?.checklist?.id,
    fetchPolicy: 'network-only',
  });

  const { data: cleaningChecklistCompletionsData, refetch: refetchCleaningChecklistCompletions } = useQuery(GET_JOB_CHECKLIST_COMPLETIONS, {
    variables: { jobId: associatedCleaningJob?.id },
    skip: !associatedCleaningJob?.id || !associatedCleaningJob?.serviceType?.useChecklistStyle || !associatedCleaningJob?.serviceType?.checklist?.id,
    fetchPolicy: 'network-only',
  });

  const isChecklistCompleteFromRecords = (targetJob, completionsData) => {
    if (!targetJob?.serviceType?.checklist?.items?.length || !targetJob?.serviceType?.checklistId) {
      return false;
    }

    const completion = completionsData?.getJobChecklistCompletions?.find(
      entry => entry.checklistId === targetJob.serviceType.checklist.id
    );

    if (!completion?.items?.length) {
      return false;
    }

    const completionMap = new Map(
      completion.items.map(item => [item.itemId, item.completed])
    );

    const requiredItems = targetJob.serviceType.checklist.items.filter(item => item.isRequired);
    if (requiredItems.length === 0) {
      return false;
    }

    return requiredItems.every(item => completionMap.get(item.id) === true);
  };
  // Function to handle photo click and open viewer
  const handlePhotoClick = (index) => {
    setCurrentPhotoIndex(index);
    setIsPhotoViewerOpen(true);
  };
  
  // Function to navigate to previous photo
  const handlePrevPhoto = () => {
    if (!job?.property?.stagingPhotos?.length) return;
    setCurrentPhotoIndex((prevIndex) => 
      prevIndex === 0 ? job.property.stagingPhotos.length - 1 : prevIndex - 1
    );
  };
  
  // Function to navigate to next photo
  const handleNextPhoto = () => {
    if (!job?.property?.stagingPhotos?.length) return;
    setCurrentPhotoIndex((prevIndex) => 
      prevIndex === job.property.stagingPhotos.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Touch event handlers for swipe gestures
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      handleNextPhoto();
    }
    
    if (isRightSwipe) {
      handlePrevPhoto();
    }
    
    // Reset values
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Close the photo viewer on escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsPhotoViewerOpen(false);
      } else if (e.key === 'ArrowLeft') {
        handlePrevPhoto();
      } else if (e.key === 'ArrowRight') {
        handleNextPhoto();
      }
    };

    if (isPhotoViewerOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPhotoViewerOpen, job?.property?.stagingPhotos]);
  
  // Function to log job data for debugging
  const logJobData = (data) => {
    // Record data silently without logging
  };

  const { managers: displayPropertyManagers } = usePropertyManagementDisplay(job?.property);

  const handleCallManager = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  // Get the status history sorted by timestamp
  const sortedStatusHistory = job?.statusHistory ? 
    [...job.statusHistory].sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    ) : [];

  // Get the most recent IN_PROGRESS status history entry to show when the job was started
  // Only show if the job is currently IN_PROGRESS or COMPLETED
  const startTimeEntry = (job?.status === 'IN_PROGRESS' || job?.status === 'COMPLETED') 
    ? sortedStatusHistory.find(history => history.status === 'IN_PROGRESS')
    : null;
  
  // Get the most recent COMPLETED status history entry to show when the job was completed
  // Only show if the job is currently COMPLETED
  const completedTimeEntry = job?.status === 'COMPLETED' 
    ? sortedStatusHistory.find(history => history.status === 'COMPLETED')
    : null;

  const completionStatusEntry =
    job?.status === 'COMPLETED'
      ? sortedStatusHistory.find((history) => history.status === 'COMPLETED')
      : job?.status === 'READY_FOR_INSPECTION'
        ? sortedStatusHistory.find((history) => history.status === 'READY_FOR_INSPECTION')
        : null;

  // Fetch property reservations - authenticated view
  const { loading: reservationsLoading, error: reservationsError, data: reservationsData } = useQuery(GET_PROPERTY_RESERVATIONS, {
    variables: { propertyId: job?.property?.id },
    skip: !job?.property?.id || isPublicView,
    fetchPolicy: 'network-only'
  });

  // Fetch property reservations - public view
  const { loading: publicReservationsLoading, error: publicReservationsError, data: publicReservationsData } = useQuery(GET_PUBLIC_PROPERTY_RESERVATIONS, {
    variables: { 
      propertyId: job?.property?.id,
      jobId: jobId,
      token: accessToken 
    },
    skip: !job?.property?.id || !isPublicView || !accessToken,
    fetchPolicy: 'network-only',
    onError: (error) => {
      console.error('Error loading public property reservations:', {
        error: error.message,
        networkError: error.networkError ? (error.networkError.message || 'Network error') : null,
        graphQLErrors: error.graphQLErrors ? error.graphQLErrors.map(e => e.message) : [],
        propertyId: job?.property?.id,
        jobId: jobId,
        tokenProvided: !!accessToken
      });
    }
  });

  // Fetch the next reservation from the server
  const { data: nextReservationData, loading: nextReservationLoading, error: nextReservationError } = useQuery(GET_NEXT_RESERVATION, {
    variables: { 
      propertyId: job?.property?.id, 
      date: job?.reservation?.check_out 
    },
    skip: !job?.property?.id || !job?.reservation?.check_out,
    fetchPolicy: 'cache-first' // Changed from 'cache-and-network' - reservation data is relatively static
  });

  // Use the next reservation from the server query
  const nextReservation = nextReservationData?.nextReservationForProperty;

  // Regular status update mutation for authenticated users
  const [updateJobStatus] = useMutation(UPDATE_JOB_STATUS, {
    refetchQueries: ['GetJob'],
    onCompleted: () => {
      // Force cache invalidation for all job-related queries
      // This ensures MySchedule and Schedule pages update immediately
      try {
        // Clear all jobs-related cache entries
        client.cache.evict({ fieldName: 'jobs' });
        client.cache.evict({ fieldName: 'jobsForAssignments' });
        client.cache.evict({ fieldName: 'readyForInspectionJobs' });
        client.cache.evict({ fieldName: 'completedJobs' });
        client.cache.evict({ fieldName: 'deletedJobs' });
        
        // 🎯 CRITICAL: Clear MySchedule cache to fix job status refresh issue
        client.cache.evict({ fieldName: 'myScheduleJobs' });
        
        // 🎯 CRITICAL: Clear optimized assigned tasks caches when job status changes
        client.cache.evict({ fieldName: 'assignedEmployeesSummary' });
        client.cache.evict({ fieldName: 'employeeAssignedJobs' });
        
        // Run garbage collection to clean up
        client.cache.gc();
      } catch (error) {
        console.error('Error invalidating cache after job status update:', error);
      }
    }
  });

  // Public status update mutation for shared links
  const [updatePublicJobStatus] = useMutation(UPDATE_PUBLIC_JOB_STATUS, {
    refetchQueries: ['GetPublicJob'],
    onCompleted: () => {
      // Force cache invalidation for all job-related queries
      // This ensures MySchedule and Schedule pages update immediately
      try {
        // Clear all jobs-related cache entries
        client.cache.evict({ fieldName: 'jobs' });
        client.cache.evict({ fieldName: 'jobsForAssignments' });
        client.cache.evict({ fieldName: 'readyForInspectionJobs' });
        client.cache.evict({ fieldName: 'completedJobs' });
        client.cache.evict({ fieldName: 'deletedJobs' });
        
        // 🎯 CRITICAL: Clear MySchedule cache to fix job status refresh issue
        client.cache.evict({ fieldName: 'myScheduleJobs' });
        
        // 🎯 CRITICAL: Clear optimized assigned tasks caches when job status changes
        client.cache.evict({ fieldName: 'assignedEmployeesSummary' });
        client.cache.evict({ fieldName: 'employeeAssignedJobs' });
        
        // Run garbage collection to clean up
        client.cache.gc();
      } catch (error) {
        console.error('Error invalidating cache after public job status update:', error);
      }
    }
  });

  const executeStartClean = async (location) => {
    try {
      const gps = buildJobStatusGPSInput(location);
      if (isPublicView) {
        await updatePublicJobStatus({
          variables: {
            id: jobId,
            status: 'IN_PROGRESS',
            token: accessToken,
            gps,
          },
        });
      } else {
        await updateJobStatus({
          variables: {
            id: jobId,
            status: 'IN_PROGRESS',
            gps,
          },
        });
      }
      toast.success('Job started successfully');
      if (onJobStatusChange) {
        onJobStatusChange(true);
      }
    } catch (error) {
      console.error('Error updating job status:', error);
      toast.error('Failed to start job');
    }
  };

  const handleStartClean = async () => {
    const location = await captureJobLocationSilently();
    await executeStartClean(location);
  };

  const executeCompleteClean = async (location) => {
    try {
      const newStatus = job.category === 'CLEANING' ? 'READY_FOR_INSPECTION' : 'COMPLETED';
      const gps = buildJobStatusGPSInput(location);
      if (isPublicView) {
        await updatePublicJobStatus({
          variables: {
            id: jobId,
            status: newStatus,
            token: accessToken,
            gps,
          },
        });
      } else {
        await updateJobStatus({
          variables: {
            id: jobId,
            status: newStatus,
            gps,
          },
        });
      }
      if (newStatus === 'READY_FOR_INSPECTION') {
        toast.success('Job marked as ready for inspection');
      } else {
        toast.success('Job completed successfully');
      }
      if (onJobStatusChange) {
        onJobStatusChange(true);
      }
    } catch (error) {
      console.error('Error completing job:', error);
      toast.error('Failed to update job status');
    }
  };

  const handleCompleteClean = async () => {
    const location = await captureJobLocationSilently();
    await executeCompleteClean(location);
  };

  // Add mutation for marking inspection complete
  const [markInspectionComplete] = useMutation(MARK_INSPECTION_COMPLETE, {
    refetchQueries: ['GetJob', 'GetAssociatedCleaningJob'],
    onCompleted: () => {
      // Force cache invalidation for all job-related queries
      // This ensures MySchedule and Schedule pages update immediately
      try {
        // Clear all jobs-related cache entries
        client.cache.evict({ fieldName: 'jobs' });
        client.cache.evict({ fieldName: 'jobsForAssignments' });
        client.cache.evict({ fieldName: 'readyForInspectionJobs' });
        client.cache.evict({ fieldName: 'completedJobs' });
        client.cache.evict({ fieldName: 'deletedJobs' });
        
        // 🎯 CRITICAL: Clear MySchedule cache to fix job status refresh issue
        client.cache.evict({ fieldName: 'myScheduleJobs' });
        
        // 🎯 CRITICAL: Clear optimized assigned tasks caches when job status changes
        client.cache.evict({ fieldName: 'assignedEmployeesSummary' });
        client.cache.evict({ fieldName: 'employeeAssignedJobs' });
        
        // Run garbage collection to clean up
        client.cache.gc();
      } catch (error) {
        console.error('Error invalidating cache after inspection complete:', error);
      }
    }
  });

  const executeMarkInspectionComplete = async (location) => {
    try {
      if (isPublicView) {
        return;
      }
      const isStandaloneInspection = !associatedCleaningJob;
      const notes = isStandaloneInspection
        ? 'Standalone inspection completed'
        : 'Inspection completed with all required checklists';
      const gps = buildJobStatusGPSInput(location);
      await markInspectionComplete({
        variables: {
          id: jobId,
          notes,
          gps,
        },
      });
      
      // Provide appropriate success message based on what happened
      if (isStandaloneInspection) {
        toast.success('Inspection completed successfully');
      } else {
        const isCleaningReady = associatedCleaningJob?.status === 'READY_FOR_INSPECTION';
        if (isCleaningReady) {
          toast.success('Inspection and associated cleaning job marked as complete');
        } else {
          toast.success('Inspection marked as complete (cleaning job remains pending)');
        }
      }
      
      // Refetch the job data to update the UI
      if (isPublicView) {
        publicJobRefetch();
      } else {
        privateJobRefetch();
      }
    } catch (error) {
      console.error('Error completing inspection:', error);
      
      // Show a more specific error message if available
      if (error.message?.includes('checklist has not been submitted')) {
        toast.error(error.message);
      } else {
        toast.error('Failed to complete inspection. Please try again.');
      }
    }
  };

  const handleMarkInspectionComplete = async () => {
    const location = await captureJobLocationSilently();
    await executeMarkInspectionComplete(location);
  };

  // Function to check if checklist has been submitted
  const isChecklistSubmitted = () => {
    // For inspection jobs, check both inspection and cleaning checklists
    if (isInspectionJob) {
      const inspectionComplete = job?.serviceType?.useChecklistStyle
        ? isChecklistCompleteFromRecords(job, jobChecklistCompletionsData)
        : !!job?.statusHistory?.find(entry => 
            entry.notes && (
              entry.notes.includes('Inspection checklist completed') ||
              entry.notes.includes('inspection checklist completed') ||
              entry.notes.includes('Step 2: Inspection checklist completed')
            )
          );

      const cleaningComplete = associatedCleaningJob?.serviceType?.useChecklistStyle
        ? isChecklistCompleteFromRecords(associatedCleaningJob, cleaningChecklistCompletionsData)
        : !!job?.statusHistory?.find(entry => 
            entry.notes && (
              entry.notes.includes('Cleaning checklist completed') ||
              entry.notes.includes('cleaning checklist completed') ||
              entry.notes.includes('Step 1: Cleaning checklist completed')
            )
          ) || !!associatedCleaningJob?.statusHistory?.find?.(entry =>
            entry.notes && (
              entry.notes.includes('Cleaning checklist completed') ||
              entry.notes.includes('cleaning checklist completed') ||
              entry.notes.includes('Step 1: Cleaning checklist completed')
            )
          );

      if (associatedCleaningJob != null) {
        return inspectionComplete && cleaningComplete;
      }

      return inspectionComplete;
    }
    
    if (job?.serviceType?.useChecklistStyle) {
      return isChecklistCompleteFromRecords(job, jobChecklistCompletionsData);
    }

    const checklistSubmissionEntry = job?.statusHistory?.find(entry => 
      entry.notes && (
        entry.notes.includes('checklist') || 
        entry.notes.includes('Checklist') || 
        entry.notes.includes('completed checklist')
      )
    );
    return !!checklistSubmissionEntry;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      // Ensure we have a valid date string
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date format';
    }
  };
  
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      // If the date is today, just show "Today"
      if (isToday(date)) {
        return 'Today';
      }
      
      // Otherwise show the date in a nice format
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date format';
    }
  };
  
  const formatTimeOnly = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '';
      }
      return format(date, 'h:mm a');
    } catch (error) {
      console.error('Error formatting time:', error);
      return '';
    }
  };

  // Convert 24-hour time format (16:00) to 12-hour format (4:00 PM)
  const convert24HourTo12Hour = (timeString) => {
    if (!timeString) return '';
    try {
      // Check if the timeString is already in 12-hour format
      if (timeString.toLowerCase().includes('am') || timeString.toLowerCase().includes('pm')) {
        return timeString;
      }
      
      // Handle 24-hour format (e.g., "16:00")
      const [hours, minutes] = timeString.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) {
        return timeString; // Return original if parsing fails
      }
      
      const period = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      console.error('Error converting time format:', error, timeString);
      return timeString; // Fall back to original value on error
    }
  };

  const [isJobChecklistModalOpen, setIsJobChecklistModalOpen] = useState(false);

  const [generateToken] = useMutation(GENERATE_SHARE_TOKEN);
  const [shareToken, setShareToken] = useState('');
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);

  // Create a shareable link that includes a token
  const generateShareableLink = async () => {
    setIsGeneratingToken(true);
    try {
      // Generate a token for this job
      const { data } = await generateToken({
        variables: {
          input: { jobId }
        }
      });
      
      // Extract the token from the response
      const token = data.generateShareToken.token;
      setShareToken(token);
      
      // Create the shareable link with the token - note we use the public route
      const baseUrl = window.location.origin;
      const shareLink = `${baseUrl}/shared/jobs/${jobId}?token=${token}`;
      setShareUrl(shareLink);
      setIsGeneratingToken(false);
      return shareLink;
    } catch (error) {
      console.error('Error generating share token:', error);
      // Fallback to a simple share URL without token
      const baseUrl = window.location.origin;
      // Still use the public route even in fallback mode
      const shareLink = `${baseUrl}/shared/jobs/${jobId}?share=true`;
      setShareUrl(shareLink);
      setIsGeneratingToken(false);
      return shareLink;
    }
  };
  
  // Handle share button click for mobile or desktop
  const handleShare = async () => {
    const shareLink = await generateShareableLink();
    
    // Check if the Web Share API is available (primarily mobile devices)
    if (navigator.share) {
      try {
        await navigator.share({
          title: job?.property?.name ? `Job at ${job.property.name}` : 'Job Details',
          text: `Check out this cleaning job${job?.property?.name ? ` at ${job.property.name}` : ''}`,
          url: shareLink
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // On desktop, show a modal with the link
      setShareModalOpen(true);
    }
  };
  
  // Copy to clipboard function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Add the handler for work order modal close
  const handleWorkOrderModalClose = (wasSuccessful) => {
    setIsCreateWorkOrderModalOpen(false);
    if (wasSuccessful) {
      toast.success('Work order created successfully');
    }
  };

  // Add handler for lost and found modal close
  const handleLostFoundModalClose = (wasSuccessful) => {
    setIsCreateLostFoundModalOpen(false);
    if (wasSuccessful) {
      toast.success('Lost and found item created successfully');
    }
  };

  // Function to handle opening the checklist modal
  const handleOpenChecklistModal = () => {
    setIsChecklistModalOpen(true);
  };
  
  // Function to handle closing the checklist modal
  const handleCloseChecklistModal = () => {
    setIsChecklistModalOpen(false);
    // Refetch job data to update checklist status
    if (isPublicView) {
      publicJobRefetch();
    } else {
      privateJobRefetch();
    }
    if (job?.serviceType?.useChecklistStyle) {
      refetchJobChecklistCompletions();
    }
    if (associatedCleaningJob?.serviceType?.useChecklistStyle) {
      refetchCleaningChecklistCompletions();
    }
  };

  if (jobLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  if (jobError) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                Error loading job details: {jobError.message}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="p-4">
        <div className="text-center">
          <p className="text-gray-500">Job not found</p>
        </div>
      </div>
    );
  }

  const isInProgress = job.status === 'IN_PROGRESS';
  const isCompleted = job.status === 'COMPLETED';
  const isReadyForInspection = job.status === 'READY_FOR_INSPECTION';
  
  // Function to determine if the related inspection job is completed
  const hasInspectionComplete = () => {
    // For cleaning jobs, check if there's a status history item mentioning inspection completed
    if (job.category === 'CLEANING') {
      return job.statusHistory?.some(item => 
        item.notes && (
          item.notes.toLowerCase().includes('inspection complete') ||
          item.notes.toLowerCase().includes('inspection finalized')
        )
      );
    }
    return false;
  };

  // Update the function to consider ALL cleaning jobs as associated, not just those with specific statuses
  const hasReadyCleaningJob = () => {
    // Just check if there's an associated cleaning job at all, regardless of status
    if (!isInspectionJob || !associatedCleaningJob) return false;
    
    // Return true regardless of status - we always want to show it
    return true;
  };
  
  // Determine if the job has a completed inspection
  const isInspectionComplete = hasInspectionComplete();
  
  // Check if this inspection job has an associated cleaning job
  const hasAssociatedCleaningJob = associatedCleaningJob != null;
  
  // Check if the cleaning job is ready for inspection specifically
  const isCleaningJobReadyForInspection = 
    hasAssociatedCleaningJob && 
    (associatedCleaningJob.status === 'READY_FOR_INSPECTION' || associatedCleaningJob.status === 'COMPLETED');
  
  // Helper to get status display text
  const getStatusText = () => {
    if (isInspectionComplete) return "Inspection Complete";
    if (isReadyForInspection) return "Ready for Inspection";
    if (isCompleted) return "Job Completed";
    if (isInProgress) return "In Progress";
    // Special case for inspection jobs with ready cleaning jobs
    if (isInspectionJob && isCleaningJobReadyForInspection) return "Ready to Inspect";
    return "Pending";
  };
  
  // Helper to get status color class
  const getStatusColorClass = () => {
    if (isInspectionComplete) return "bg-purple-600";
    if (isReadyForInspection) return "bg-indigo-600";
    if (isCompleted) return "bg-green-600";
    if (isInProgress) return "bg-sky-600";
    // Special case for inspection jobs with ready cleaning jobs
    if (isInspectionJob && isCleaningJobReadyForInspection) return "bg-indigo-600";
    return "bg-yellow-600";
  };

  return (
    <div className="min-h-screen">
      <JobHeader 
        job={job}
        isSharedView={isSharedView}
        startTimeEntry={startTimeEntry}
        completedTimeEntry={completedTimeEntry}
        completionStatusEntry={completionStatusEntry}
        isInProgress={isInProgress}
        isCompleted={isCompleted}
        isReadyForInspection={isReadyForInspection}
        isInspectionJob={isInspectionJob}
        isInspectionComplete={isInspectionComplete}
        isChecklistSubmitted={isChecklistSubmitted}
        handleStartClean={handleStartClean}
        handleCompleteClean={handleCompleteClean}
        handleMarkInspectionComplete={handleMarkInspectionComplete}
        handleOpenChecklistModal={handleOpenChecklistModal}
        handleShare={handleShare}
        formatDateTime={formatDateTime}
        getStatusText={getStatusText}
        getStatusColorClass={getStatusColorClass}
      />

      {/* Main content area */}
      <div className="max-w-7xl mx-auto pb-6">
        <div className="px-2 lg:px-4 py-3 lg:py-6">
          {/* Job Meta Information */}
          <JobMeta 
            job={job}
            nextReservation={nextReservation}
            nextReservationLoading={nextReservationLoading}
            formatDateForDisplay={formatDateForDisplay}
            formatTimeOnly={formatTimeOnly}
            convert24HourTo12Hour={convert24HourTo12Hour}
            getServiceTypeDisplayName={getServiceTypeDisplayName}
            isPublicView={isPublicView}
          />

          <JobChecklistSection
            job={job}
            viewOnly={isSharedView || (job?.category === 'CLEANING' && !job?.serviceType?.useChecklistStyle)}
            onChecklistUpdate={() => {
              refetchJobChecklistCompletions();
            }}
          />

          {/* Admin Actions Bar - Only visible to admins, property managers, and housekeeper managers */}
          <JobAdminBar 
            job={job}
            onJobUpdate={() => {
              // Refetch job data when updates are made
              if (isPublicView) {
                publicJobRefetch();
              } else {
                privateJobRefetch();
              }
            }}
          />

          {/* Important Note Section - Display only when job has notes */}
          {job.notes && (
            <div className="bg-white shadow rounded-lg mb-3 lg:mb-6">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center">
                  <svg className="h-6 w-6 text-yellow-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Important Note
                </h3>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                  <div className="flex">
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 whitespace-pre-line">
                        {job.notes}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Property Information with Address, Access and Payout Info */}
          <JobPropertyInfo job={job} />

          {/* Housekeeping Status section - Show only for inspection jobs */}
          {isInspectionJob && !isPublicView && (
            <div className="bg-white shadow rounded-lg mb-3 lg:mb-6">
              <div className="px-4 py-5 sm:p-6">
                {/* Associated Cleaning Job or Standalone Inspection */}
                <div>
                  <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center">
                    {hasAssociatedCleaningJob ? 'Associated Cleaning Job' : 'Standalone Inspection'}
                    {hasAssociatedCleaningJob ? (
                      job.reservationId && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                          Reservation: {job.reservationId.substring(0, 8)}...
                        </span>
                      )
                    ) : (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-100 text-emerald-800">
                        No Cleaning Required
                      </span>
                    )}
                  </h4>
                  {associatedCleaningJobLoading ? (
                    <div className="flex items-center justify-center h-24 bg-gray-50 rounded-lg">
                      <svg className="animate-spin h-6 w-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  ) : associatedCleaningJob ? (
                    <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                      <div className="flex flex-col space-y-3">
                        {/* Property Title */}
                        <div className="text-base font-medium text-gray-900">
                          {associatedCleaningJob.property?.name || 
                           (job.property?.name ? job.property.name : 'No property name available')}
                        </div>

                        {/* Job Date */}
                        <div className="flex items-center text-sm text-gray-700">
                          <svg className="h-4 w-4 mr-1.5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDateForDisplay(associatedCleaningJob.scheduledDate)}
                        </div>

                        {/* Assignment Status */}
                        <div className="flex items-center text-sm text-gray-700">
                          <svg className="h-4 w-4 mr-1.5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {associatedCleaningJob.assignedTo ? (
                            <span>{associatedCleaningJob.assignedTo.name}</span>
                          ) : (
                            <span className="text-yellow-600">Not yet assigned</span>
                          )}
                        </div>

                        {/* Job Status */}
                        <div className="mt-2">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium 
                            ${associatedCleaningJob.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 
                              associatedCleaningJob.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' : 
                              associatedCleaningJob.status === 'READY_FOR_INSPECTION' ? 'bg-indigo-100 text-indigo-800' :
                              associatedCleaningJob.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' : 
                              'bg-gray-100 text-gray-800'}`}>
                            {associatedCleaningJob.status === 'PENDING' && 'PENDING CLEANING'}
                            {associatedCleaningJob.status === 'IN_PROGRESS' && 'CLEANING IN PROGRESS'}
                            {associatedCleaningJob.status === 'READY_FOR_INSPECTION' && 'READY FOR INSPECTION'}
                            {associatedCleaningJob.status === 'COMPLETED' && 'CLEANING COMPLETED'}
                            {!['PENDING', 'IN_PROGRESS', 'READY_FOR_INSPECTION', 'COMPLETED'].includes(associatedCleaningJob.status) && 
                              associatedCleaningJob.status?.replace('_', ' ')}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="mt-4">
                        {/* Only show "View Cleaning Job" button to ADMIN role */}
                        {user?.role === 'ADMIN' && (
                          <a 
                            href={`/dashboard/jobs/${associatedCleaningJob.id}`}
                            className="inline-flex items-center px-3 py-1.5 border border-indigo-500 text-xs font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            View Cleaning Job
                          </a>
                        )}
                        
                        {/* Add support for all status types */}
                        {associatedCleaningJob.status === 'PENDING' && !isCompleted && (
                          <div className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-500 bg-white ml-2">
                            Cleaning Not Started
                          </div>
                        )}

                        {associatedCleaningJob.status === 'IN_PROGRESS' && !isCompleted && (
                          <div className="inline-flex items-center px-3 py-1.5 border border-sky-300 text-xs font-medium rounded-md text-sky-600 bg-white ml-2">
                            Cleaning In Progress
                          </div>
                        )}
                        
                        {(associatedCleaningJob.status === 'READY_FOR_INSPECTION' || associatedCleaningJob.status === 'COMPLETED') && !isCompleted && (
                          <button
                            onClick={handleMarkInspectionComplete}
                            disabled={!isChecklistSubmitted()}
                            className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-md ml-2
                              ${isChecklistSubmitted() ? 'border-green-500 text-green-600 bg-white hover:bg-green-50' : 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'}`}
                          >
                            Complete Inspection
                          </button>
                        )}
                        
                        {/* Allow inspection completion even when cleaning job is not ready */}
                        {associatedCleaningJob.status !== 'READY_FOR_INSPECTION' && 
                         associatedCleaningJob.status !== 'COMPLETED' && 
                         !isCompleted && (
                          <button
                            onClick={handleMarkInspectionComplete}
                            disabled={!isChecklistSubmitted()}
                            className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-md ml-2
                              ${isChecklistSubmitted() ? 'border-orange-500 text-orange-600 bg-white hover:bg-orange-50' : 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'}`}
                          >
                            Complete Inspection Only
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 rounded-lg p-4 shadow-sm border border-emerald-200">
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-3">
                          <svg className="h-8 w-8 text-emerald-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <h5 className="text-emerald-800 font-medium">Standalone Pre-arrival Inspection</h5>
                        </div>
                        <p className="text-emerald-700 text-sm mb-3">
                          This inspection can be completed independently without an associated cleaning job.
                        </p>
                        <p className="text-emerald-600 text-xs">
                          Complete the inspection checklist below to finish this job.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Home Information section */}
          <JobHomeDetails 
            job={job}
            propertyManagers={displayPropertyManagers}
            handleCallManager={handleCallManager}
          />

          {/* Property Notes Section */}
          <JobPropertyNotes job={job} />

          {/* Property Staging Photos Section */}
          <JobStagingPhotos 
            job={job}
            handlePhotoClick={handlePhotoClick}
            isPhotoViewerOpen={isPhotoViewerOpen}
            currentPhotoIndex={currentPhotoIndex}
            setIsPhotoViewerOpen={setIsPhotoViewerOpen}
            handlePrevPhoto={handlePrevPhoto}
            handleNextPhoto={handleNextPhoto}
            handleTouchStart={handleTouchStart}
            handleTouchMove={handleTouchMove}
            handleTouchEnd={handleTouchEnd}
          />

          {/* Report an Issue section - Only show in authenticated view */}
          {!isSharedView && (
            <div className="bg-white shadow rounded-lg mb-3 lg:mb-6">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Property Actions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Report Issue Column */}
                  <div className="bg-gray-50 rounded-lg p-5 shadow-sm">
                    <h4 className="text-base font-medium text-gray-900 mb-2">
                      Report an Issue
                    </h4>
                    <p className="text-sm text-gray-500 mb-4">
                      If you notice any issues with the property during your visit, please create a work order so we can address it.
                    </p>
                    <button
                      onClick={() => setIsCreateWorkOrderModalOpen(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7h1a2 2 0 012 2v9a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h1m0 0V5a3 3 0 016 0v2m-3 10v-2m0 0l3-3m-3 3l-3-3" />
                      </svg>
                      Create Work Order
                    </button>
                  </div>

                  {/* Lost and Found Column */}
                  <div className="bg-gray-50 rounded-lg p-5 shadow-sm">
                    <h4 className="text-base font-medium text-gray-900 mb-2">
                      Found an Item
                    </h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Found an item left behind by a guest? Report it here to add it to the lost and found inventory.
                    </p>
                    <button
                      onClick={() => setIsCreateLostFoundModalOpen(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                    >
                      <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                      </svg>
                      Report Lost Item
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          

          

        {/* Reset Job Section */}
        <div className="mt-8 px-4 sm:px-0">
          <div className="border-t border-gray-200 pt-8">
              {/* Job Status History Timeline */}
              {!isSharedView && (
                <div className="mb-8">
                  <h3 className="text-lg leading-6 font-medium text-gray-200 lg:text-gray-900 mb-4">
                    Job History
                  </h3>
                  
                  {/* Current Job Status Summary - simplified */}
                  <div className="mb-4 rounded-md p-4" style={{
                    backgroundColor: job.status === 'PENDING' ? '#f9fafb' : 
                                    job.status === 'IN_PROGRESS' ? '#eff6ff' : 
                                    job.status === 'READY_FOR_INSPECTION' ? '#eef2ff' : 
                                    job.status === 'COMPLETED' ? '#ecfdf5' : '#f9fafb',
                    borderWidth: '1px',
                    borderColor: job.status === 'PENDING' ? '#e5e7eb' : 
                                job.status === 'IN_PROGRESS' ? '#bfdbfe' : 
                                job.status === 'READY_FOR_INSPECTION' ? '#c7d2fe' : 
                                job.status === 'COMPLETED' ? '#a7f3d0' : '#e5e7eb'
                  }}>
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <span className="h-8 w-8 rounded-full flex items-center justify-center" style={{
                          backgroundColor: job.status === 'PENDING' ? '#9ca3af' : 
                                         job.status === 'IN_PROGRESS' ? '#3b82f6' : 
                                         job.status === 'READY_FOR_INSPECTION' ? '#6366f1' : 
                                         job.status === 'COMPLETED' ? '#10b981' : '#9ca3af'
                        }}>
                          {job.status === 'COMPLETED' && (
                            <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {job.status === 'IN_PROGRESS' && (
                            <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 3a7 7 0 107 7 1 1 0 112 0A9 9 0 1110 1a1 1 0 100 2zm0 4a1 1 0 011 1v3.586l2.707 2.707a1 1 0 01-1.414 1.414l-3-3A1 1 0 019 10V8a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                          )}
                          {job.status === 'PENDING' && (
                            <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                          )}
                          {job.status === 'READY_FOR_INSPECTION' && (
                            <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 3a1 1 0 012 0v5.5a.5.5 0 001 0V4a1 1 0 112 0v4.5a.5.5 0 001 0V6a1 1 0 112 0v5a7 7 0 11-14 0V9a1 1 0 012 0v2.5a.5.5 0 001 0V4a1 1 0 012 0v4.5a.5.5 0 001 0V3z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>
                      </div>
                      <div className="ml-3">
                        {job.status === 'PENDING' && (
                          <p className="text-sm font-medium text-gray-900">
                            Status: <span className="font-semibold">Pending</span>
                            {(job.created_at || job.createdAt) && (
                              <span className="ml-1 text-gray-500">
                                (Job created {formatDateTime(job.created_at || job.createdAt)})
                              </span>
                            )}
                          </p>
                        )}
                        
                        {job.status === 'IN_PROGRESS' && (
                          <p className="text-sm font-medium text-gray-900">
                            Status: <span className="font-semibold">In Progress</span>
                            {startTimeEntry && (
                              <span className="ml-1 text-gray-500">
                                (Started {formatDateTime(startTimeEntry.timestamp)})
                              </span>
                            )}
                          </p>
                        )}
                        
                        {job.status === 'READY_FOR_INSPECTION' && (
                          <p className="text-sm font-medium text-gray-900">
                            Status: <span className="font-semibold">Ready for Inspection</span>
                            {/* Find the ready for inspection status entry */}
                            {job.statusHistory.find(history => history.status === 'READY_FOR_INSPECTION') && (
                              <span className="ml-1 text-gray-500">
                                (Cleaning completed {formatDateTime(job.statusHistory.find(history => history.status === 'READY_FOR_INSPECTION').timestamp)})
                              </span>
                            )}
                          </p>
                        )}
                        
                        {job.status === 'COMPLETED' && (
                          <p className="text-sm font-medium text-gray-900">
                            Status: <span className="font-semibold">Completed</span>
                            {completedTimeEntry && (
                              <span className="ml-1 text-gray-500">
                                (Finalized {formatDateTime(completedTimeEntry.timestamp)}
                                {completedTimeEntry.updatedBy && <span> by {completedTimeEntry.updatedBy.name}</span>})
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Only show detailed history if there are more than 1 status changes */}
                  {job.statusHistory && job.statusHistory.length > 1 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Status Change History</h4>
                      <div className="flow-root">
                        <ul className="-mb-8">
                          {[...job.statusHistory] // Create a copy of the array before sorting
                            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()) // Sort newest first
                            .map((historyItem, historyIdx) => (
                              <li key={historyItem.id || historyIdx}>
                                <div className="relative pb-8">
                                  {historyIdx !== job.statusHistory.length - 1 ? (
                                    <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                                  ) : null}
                                  <div className="relative flex space-x-3">
                                    <div>
                                      <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                                        historyItem.status === 'COMPLETED' 
                                          ? 'bg-green-500' 
                                          : historyItem.status === 'IN_PROGRESS' 
                                            ? 'bg-blue-500' 
                                            : historyItem.status === 'PENDING' 
                                              ? 'bg-gray-500' 
                                              : historyItem.status === 'READY_FOR_INSPECTION'
                                                ? 'bg-indigo-500'
                                                : 'bg-yellow-500'
                                      }`}>
                                        {historyItem.status === 'COMPLETED' && (
                                          <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                        {historyItem.status === 'IN_PROGRESS' && (
                                          <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 3a7 7 0 107 7 1 1 0 112 0A9 9 0 1110 1a1 1 0 100 2zm0 4a1 1 0 011 1v3.586l2.707 2.707a1 1 0 01-1.414 1.414l-3-3A1 1 0 019 10V8a1 1 0 011-1z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                        {historyItem.status === 'PENDING' && (
                                          <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                        {historyItem.status === 'READY_FOR_INSPECTION' && (
                                          <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M9 3a1 1 0 012 0v5.5a.5.5 0 001 0V4a1 1 0 112 0v4.5a.5.5 0 001 0V6a1 1 0 112 0v5a7 7 0 11-14 0V9a1 1 0 012 0v2.5a.5.5 0 001 0V4a1 1 0 012 0v4.5a.5.5 0 001 0V3z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                      </span>
                                    </div>
                                    <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                      <div>
                                        <p className="text-sm text-gray-900">
                                          {historyItem.status === 'PENDING' && 'Job created'}
                                          {historyItem.status === 'IN_PROGRESS' && 'Clean started'}
                                          {historyItem.status === 'READY_FOR_INSPECTION' && 'Clean completed, ready for inspection'}
                                          {historyItem.status === 'COMPLETED' && 'Job completed'}
                                          {historyItem.status !== 'PENDING' && 
                                           historyItem.status !== 'IN_PROGRESS' && 
                                           historyItem.status !== 'READY_FOR_INSPECTION' && 
                                           historyItem.status !== 'COMPLETED' && 
                                           `Status changed to ${historyItem.status.replace(/_/g, ' ')}`}
                                          
                                          {historyItem.notes && (
                                            <span className="text-gray-500"> — {historyItem.notes}</span>
                                          )}
                                        </p>
                                      </div>
                                      <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                        <div>
                                          {formatDateTime(historyItem.timestamp)}
                                        </div>
                                        {historyItem.updatedBy && historyItem.updatedBy.name && (
                                          <div className="text-xs text-gray-600 mt-1">
                                            Marked by: <span className="font-medium">{historyItem.updatedBy.name}</span>
                                          </div>
                                        )}
                                        {historyItem.updatedBy && !historyItem.updatedBy.name && (
                                          <div className="text-xs text-gray-400 mt-1 italic">
                                            (User info unavailable)
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Only show Reset Job Option if the job is not in PENDING status */}
              {job.status !== 'PENDING' && (
                <div className="rounded-md bg-yellow-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.667-1.743-2.98l5.58-9.92zM11 13a1 1 0 100-2 1 1 0 000 2zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Reset Job Status
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                            {isSharedView 
                              ? "Need to restart the clean? This will reset the job back to its initial state and clear any recorded timestamps."
                              : "Did you start this job incorrectly? This will change the job's status back to PENDING and reset the timestamps."
                            }
                        </p>
                      </div>
                      <div className="mt-4">
                        <button
                          onClick={async () => {
                            try {
                                const confirmReset = window.confirm('Are you sure you want to reset this job? This will change the status back to PENDING and clear any recorded timestamps.');
                                if (!confirmReset) return;
                                
                                if (isPublicView) {
                                  await updatePublicJobStatus({
                                    variables: {
                                      id: jobId,
                                      status: 'PENDING',
                                      token: accessToken
                                    }
                                  });
                                } else {
                              await updateJobStatus({
                                variables: {
                                  id: jobId,
                                  status: 'PENDING'
                                }
                              });
                                }
                            } catch (error) {
                              console.error('Error resetting job status:', error);
                            }
                          }}
                          className="inline-flex items-center px-3 py-2 border border-yellow-700 text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-150"
                        >
                          <svg className="mr-2 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 100-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                          </svg>
                          Reset Job
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Job Checklist Modal */}
      {isJobChecklistModalOpen && (
        <JobChecklistModal
          isOpen={isJobChecklistModalOpen}
          onClose={handleCloseChecklistModal}
        />
      )}

      {/* Checklist Modal */}
      <JobChecklistModal 
        isOpen={isChecklistModalOpen}
        onClose={handleCloseChecklistModal}
        job={job}
        viewOnly={isSharedView || (job?.category === 'CLEANING' && !job?.serviceType?.useChecklistStyle)}
        associatedCleaningJob={isInspectionJob ? associatedCleaningJob : null}
      />

      {/* Share Modal */}
      {shareModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Share Job</h3>
              <button
                onClick={() => setShareModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {isGeneratingToken ? (
              <div className="flex justify-center items-center py-6">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600"></div>
                <p className="ml-3 text-gray-600">Generating secure link...</p>
              </div>
            ) : (
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">Share this job with someone:</p>
                <div className="flex space-x-2 mb-4">
                  <a
                    href={`mailto:?subject=Job at ${job?.property?.name || 'Property'}&body=Check out this cleaning job: ${shareUrl}`}
                    className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  >
                    <svg className="h-5 w-5 mr-2 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    Email Link
                  </a>
                  <a
                    href={`sms:?body=Check out this cleaning job: ${shareUrl}`}
                    className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  >
                    <svg className="h-5 w-5 mr-2 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm14 1H4v8l4-2.5 6 2.5V6z" clipRule="evenodd" />
                    </svg>
                    Text Link
                  </a>
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Or copy this link:
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 block w-full rounded-l-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(shareUrl)}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-r-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}



      {/* Create Work Order Modal */}
      <CreateWorkOrderModal
        isOpen={isCreateWorkOrderModalOpen}
        onClose={handleWorkOrderModalClose}
        propertyId={job?.property?.id}
        propertyName={job?.property?.name}
        reservationId={job?.reservation?.id}
      />

      {/* Create Lost Found Modal */}
      <CreateLostFoundModal
        isOpen={isCreateLostFoundModalOpen}
        onClose={handleLostFoundModalClose}
        propertyId={job?.property?.id}
        propertyName={job?.property?.name}
        propertyData={job?.property}
      />
    </div>
  );
}
